import { useState, useRef, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import { addDays, format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useLocation } from 'react-router-dom';

// Fallback mock database for places search
const fetchPlaces = (query) => {
    return new Promise((resolve) => {
        resolve(getMockPlaces(query));
    });
};

// Fallback Mock Database queries
const getMockPlaces = (query) => {
    if (!query || query.length < 2) return [];
    const q = query.charAt(0).toUpperCase() + query.slice(1);
    return [
        `${q}, India`,
        `${q} International Airport`,
        `${q} Central Railway Station`,
        `Top Hotels in ${q}`
    ];
};

// Reverse geocode latitude/longitude using Nominatim API
const getCityFromLatLng = async (latitude, longitude) => {
    try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
        const data = await res.json();
        const cityName = data?.address?.city || data?.address?.town || data?.address?.village || "";
        if (cityName) {
            return cityName;
        }
        throw new Error("City name not found in Nominatim response");
    } catch (error) {
        console.error("Nominatim reverse geocoding failed:", error);
        throw error;
    }
};

const popularDestinations = [
    { city: "Goa", description: "Beaches & Nightlife", icon: "🌴" },
    { city: "Mumbai", description: "City of Dreams", icon: "🏙️" },
    { city: "New Delhi", description: "History & Heritage", icon: "🏛️" },
    { city: "Bengaluru", description: "Garden City & Tech Hub", icon: "💻" },
    { city: "Ooty", description: "Scenic Hill Station", icon: "⛰️" },
    { city: "Manali", description: "Snowy Mountains", icon: "🏔️" }
];

function HotelSearchBar({ compact = false }) {
    const [city, setCity] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [isSearchingPlaces, setIsSearchingPlaces] = useState(false);

    const [dateRange, setDateRange] = useState([{
        startDate: new Date(),
        endDate: addDays(new Date(), 1),
        key: 'selection'
    }]);

    const [openDate, setOpenDate] = useState(false);
    const [openGuest, setOpenGuest] = useState(false);
    const [rooms, setRooms] = useState(1);
    const [guests, setGuests] = useState(2);
    const [isLoading, setIsLoading] = useState(false);
    const [showMobileDateSheet, setShowMobileDateSheet] = useState(false);

    const location = useLocation();

    const dateRef = useRef(null);
    const guestRef = useRef(null);
    const cityRef = useRef(null);
    const mobileGuestRef = useRef(null);
    const mobileCityRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDate && dateRef.current && !dateRef.current.contains(event.target)) {
                setOpenDate(false);
            }
            if (openGuest && 
                (!guestRef.current || !guestRef.current.contains(event.target)) &&
                (!mobileGuestRef.current || !mobileGuestRef.current.contains(event.target))
            ) {
                setOpenGuest(false);
            }
            if (
                (!cityRef.current || !cityRef.current.contains(event.target)) &&
                (!mobileCityRef.current || !mobileCityRef.current.contains(event.target))
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDate, openGuest]);

    // Prevent body scroll when bottom sheet is open
    useEffect(() => {
        if (showMobileDateSheet) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showMobileDateSheet]);

    const handleDateChange = (item) => {
        const selection = item.selection;
        setDateRange([selection]);
        const { startDate, endDate } = selection;
        if (endDate.getTime() > startDate.getTime()) {
            setTimeout(() => {
                setOpenDate(false);
                setShowMobileDateSheet(false);
            }, 120);
        }
    };

    const handleNearbyClick = async (e) => {
        if (e) e.stopPropagation();
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser.");
            return;
        }

        setIsLoading(true);
        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject);
            });

            const { latitude, longitude } = position.coords;
            try {
                const cityName = await getCityFromLatLng(latitude, longitude);
                setCity(cityName);
            } catch (fallbackError) {
                setCity("Current Location");
            }
        } catch (error) {
            console.error("Error fetching location:", error);
            setCity("Unable to get location");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e) => {
        if (e) e.stopPropagation();
        const searchParams = new URLSearchParams({
            city: city,
            checkin: format(dateRange[0].startDate, 'yyyy-MM-dd'),
            checkout: format(dateRange[0].endDate, 'yyyy-MM-dd'),
            guests: guests.toString(),
            rooms: rooms.toString()
        });
        window.location.href = `/search?${searchParams.toString()}`;
    };

    // Debounced Places Search
    useEffect(() => {
        if (!city || city === 'Near Me') {
            setSuggestions([]);
            return;
        }

        setIsSearchingPlaces(true);
        const timerId = setTimeout(async () => {
            const results = await fetchPlaces(city);
            setSuggestions(results);
            setIsSearchingPlaces(false);
        }, 200); // 200ms debounce

        return () => {
            clearTimeout(timerId);
        };
    }, [city]);

    const getDaysDifference = () => {
        const start = dateRange[0].startDate;
        const end = dateRange[0].endDate;
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const roomsParam = params.get("rooms");
        if (roomsParam) {
            const parsedRooms = parseInt(roomsParam, 10);
            if (!isNaN(parsedRooms)) setRooms(parsedRooms);
        }

        const guestsParam = params.get("guests");
        if (guestsParam) {
            const parsedGuests = parseInt(guestsParam, 10);
            if (!isNaN(parsedGuests)) setGuests(parsedGuests);
        }

        const checkinParam = params.get("checkin");
        const checkoutParam = params.get("checkout");
        if (checkinParam && checkoutParam) {
            const checkinDate = new Date(checkinParam + 'T00:00:00');
            const checkoutDate = new Date(checkoutParam + 'T00:00:00');
            if (!isNaN(checkinDate.getTime()) && !isNaN(checkoutDate.getTime())) {
                setDateRange([{
                    startDate: checkinDate,
                    endDate: checkoutDate,
                    key: 'selection'
                }]);
            }
        }

        const cityParam = params.get("city");
        if (cityParam) {
            let decoded = cityParam;
            try {
                decoded = decodeURIComponent(cityParam);
                if (decoded.includes('%')) {
                    decoded = decodeURIComponent(decoded);
                }
            } catch (e) {
                console.error("Failed to decode city parameter:", e);
            }
            setCity(decoded);
        } else {
            // Fetch current location by default on homepage / load
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        try {
                            const cityName = await getCityFromLatLng(latitude, longitude);
                            if (cityName) {
                                setCity(cityName);
                            }
                        } catch (fallbackError) {
                            console.error("Reverse geocoding failed:", fallbackError);
                        }
                    },
                    (error) => {
                        console.error("Error getting location:", error);
                    }
                );
            }
        }
    }, [location.search]);

    return (
        <div className="container-fluid position-relative px-2">
            {/* Desktop/Tablet View - Airbnb Style Floating Pill Layout */}
            <div className={`d-none d-md-flex align-items-center bg-white rounded-pill border-1 p-1 search-bar-desktop-pill ${compact ? 'search-bar-compact' : ''}`}>

                {/* City Input Section */}
                <div
                    className={`position-relative flex-grow-1 px-4 py-2 rounded-pill search-bar-section cursor-pointer ${showSuggestions ? 'search-bar-section-active' : ''}`}
                    ref={cityRef}
                    onClick={() => {
                        setShowSuggestions(true);
                        setOpenDate(false);
                        setOpenGuest(false);
                    }}
                >
                    <div className="d-flex flex-column text-start">
                        <label className="text-[10px] uppercase font-bold text-gray-400 mb-0.5 tracking-wider">Where</label>
                        <input
                            type="text"
                            value={city}
                            onChange={(e) => {
                                setCity(e.target.value);
                                setShowSuggestions(true);
                            }}
                            className="border-0 shadow-none bg-transparent w-100 fw-bold text-gray-800 p-0"
                            placeholder="Search destinations"
                            style={{ fontSize: '14px', outline: 'none' }}
                        />
                    </div>

                    {city && city !== 'Near Me' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setCity('');
                            }}
                            className="btn btn-sm position-absolute end-0 top-50 translate-middle-y text-gray-400 p-1 hover:text-gray-600 border-0 me-3"
                            style={{ background: 'none' }}
                        >
                            <i className="bi bi-x-circle-fill" style={{ fontSize: '15px' }}></i>
                        </button>
                    )}
                    {(!city || city === 'Near Me') && (
                        <button
                            onClick={handleNearbyClick}
                            disabled={isLoading}
                            className="position-absolute end-0 top-50 translate-middle-y text-blue-600 hover:text-blue-800 p-1 hover:scale-105 transition-all border-0 me-3"
                            style={{ background: 'none' }}
                        >
                            {isLoading ? (
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                            ) : (
                                <i className="fas fa-crosshairs"></i>
                            )}
                        </button>
                    )}

                    {/* Autocomplete & Popular Places Dropdown */}
                    {showSuggestions && (
                        <div className="position-absolute top-100 start-0 w-100 text-dark bg-white border border-gray-200 rounded-3 z-3 mt-2 p-2 dropdown-animation" style={{ minWidth: '320px' }}>
                            {isSearchingPlaces ? (
                                <div className="px-3 py-4 text-center text-muted small">
                                    <div className="spinner-border spinner-border-sm text-blue-600 me-2" role="status"></div>
                                    Finding matching places...
                                </div>
                            ) : city && city !== 'Near Me' && suggestions.length > 0 ? (
                                <div className="py-1">
                                    <div className="text-[10px] uppercase font-bold text-gray-400 px-3 pb-2 tracking-wider">Search Results</div>
                                    {suggestions.map((c, i) => (
                                        <div
                                            key={i}
                                            className="px-3 py-2.5 rounded-2 d-flex align-items-center hover-bg-light transition-colors"
                                            style={{ cursor: 'pointer' }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setCity(c);
                                                setShowSuggestions(false);
                                            }}
                                        >
                                            <div className="w-8 h-8 bg-blue-50 rounded-circle flex items-center justify-center text-blue-600 me-3">
                                                <i className="bi bi-geo-alt-fill"></i>
                                            </div>
                                            <div>
                                                <div className="fw-semibold text-gray-800 text-sm">{c}</div>
                                                <div className="text-xs text-gray-400">Destination, India</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-1">
                                    <div className="text-[10px] uppercase font-bold text-gray-400 px-3 pb-2 tracking-wider">Popular Destinations</div>
                                    <div className="grid grid-cols-2 gap-1 p-1">
                                        {popularDestinations.map((dest, i) => (
                                            <div
                                                key={i}
                                                className="p-2.5 rounded-xl d-flex align-items-center hover-bg-light cursor-pointer transition-colors"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setCity(dest.city);
                                                    setShowSuggestions(false);
                                                }}
                                            >
                                                <span className="fs-4 me-2.5">{dest.icon}</span>
                                                <div className="text-start">
                                                    <div className="fw-bold text-gray-800 text-xs">{dest.city}</div>
                                                    <div className="text-[10px] text-gray-400 whitespace-nowrap">{dest.description}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Vertical Divider */}
                <div className="search-bar-divider"></div>

                {/* Date Picker Section */}
                <div
                    className={`px-4 py-2 rounded-pill search-bar-section cursor-pointer position-relative ${openDate ? 'search-bar-section-active' : ''}`}
                    ref={dateRef}
                    style={{ minWidth: '220px' }}
                    onClick={() => {
                        setOpenDate(!openDate);
                        setOpenGuest(false);
                        setShowSuggestions(false);
                    }}
                >
                    <div className="d-flex flex-column text-start">
                        <label className="text-[10px] uppercase font-bold text-gray-400 mb-0.5 tracking-wider">Dates</label>
                        <span className="fw-bold text-gray-800 text-sm">
                            {format(dateRange[0].startDate, 'dd MMM')} - {format(dateRange[0].endDate, 'dd MMM')}
                        </span>
                    </div>

                    {openDate && (
                        <div
                            className="position-absolute bg-white border border-gray-200 rounded-3 z-3 mt-2 dropdown-animation"
                            style={{ top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <DateRange
                                locale={enUS}
                                editableDateInputs={true}
                                onChange={handleDateChange}
                                moveRangeOnFirstSelection={false}
                                ranges={dateRange}
                                months={2}
                                direction="horizontal"
                                minDate={new Date()}
                                rangeColors={['#2563eb']}
                            />
                        </div>
                    )}
                </div>

                {/* Vertical Divider */}
                <div className="search-bar-divider"></div>

                {/* Guest & Room Selector Section */}
                <div
                    className={`px-4 py-2 rounded-pill search-bar-section cursor-pointer position-relative ${openGuest ? 'search-bar-section-active' : ''}`}
                    ref={guestRef}
                    style={{ minWidth: '200px' }}
                    onClick={() => {
                        setOpenGuest(!openGuest);
                        setOpenDate(false);
                        setShowSuggestions(false);
                    }}
                >
                    <div className="d-flex flex-column text-start">
                        <label className="text-[10px] uppercase font-bold text-gray-400 mb-0.5 tracking-wider">Who</label>
                        <span className="fw-bold text-gray-800 text-sm">
                            {rooms} Room{rooms > 1 ? 's' : ''}, {guests} Guest{guests > 1 ? 's' : ''}
                        </span>
                    </div>

                    {openGuest && (
                        <div
                            className="position-absolute z-3 bg-white border border-gray-200 rounded-3 p-4 text-black dropdown-animation"
                            style={{ top: '100%', right: '0', minWidth: '320px', marginTop: '8px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mb-4">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="text-start">
                                        <div className="fw-bold text-gray-800 text-sm">Rooms</div>
                                        <div className="text-xs text-gray-400">Number of rooms required</div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <button
                                            className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                            onClick={() => setRooms(Math.max(1, rooms - 1))}
                                            disabled={rooms <= 1}
                                            style={{ width: '36px', height: '36px', minWidth: '36px' }}
                                        >
                                            <i className="bi bi-dash fs-5"></i>
                                        </button>
                                        <span className="fw-bold text-gray-800 mx-3" style={{ fontSize: '16px', minWidth: '20px', textAlign: 'center' }}>{rooms}</span>
                                        <button
                                            className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                            onClick={() => setRooms(Math.min(5, rooms + 1))}
                                            disabled={rooms >= 5}
                                            style={{ width: '36px', height: '36px', minWidth: '36px' }}
                                        >
                                            <i className="bi bi-plus fs-5"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="text-start">
                                        <div className="fw-bold text-gray-800 text-sm">Guests</div>
                                        <div className="text-xs text-gray-400">Total number of travelers</div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <button
                                            className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                            onClick={() => setGuests(Math.max(1, guests - 1))}
                                            disabled={guests <= 1}
                                            style={{ width: '36px', height: '36px', minWidth: '36px' }}
                                        >
                                            <i className="bi bi-dash fs-5"></i>
                                        </button>
                                        <span className="fw-bold text-gray-800 mx-3" style={{ fontSize: '16px', minWidth: '20px', textAlign: 'center' }}>{guests}</span>
                                        <button
                                            className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                            onClick={() => setGuests(Math.min(10, guests + 1))}
                                            disabled={guests >= 10}
                                            style={{ width: '36px', height: '36px', minWidth: '36px' }}
                                        >
                                            <i className="bi bi-plus fs-5"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Search Action Button */}
                <div className="p-1">
                    <button
                        className="btn btn-primary rounded-circle search-action-round-btn d-flex align-items-center justify-content-center shadow-lg"
                        onClick={handleSearch}
                        title="Search Hotels"
                    >
                        <i className="bi bi-search text-white"></i>
                    </button>
                </div>
            </div>

            {/* Mobile View - Revamped Mobile Search Sheet */}
            <div className="d-block d-md-none bg-white p-3 rounded-2xl border border-gray-100 shadow-md text-start search-bar-mobile-card">
                {/* City Input - Mobile */}
                <div className="mb-3 position-relative" ref={mobileCityRef}>
                    <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 tracking-wider block">
                        <i className="bi bi-geo-alt-fill text-blue-600 me-1"></i>Destination
                    </label>
                    <div className="d-flex align-items-center border border-gray-200 rounded-xl px-3 py-2 shadow-sm bg-gray-50/50">
                        <input
                            type="text"
                            value={city}
                            onChange={(e) => {
                                setCity(e.target.value);
                                setShowSuggestions(true);
                            }}
                            className="border-0 flex-grow-1 fw-bold text-gray-800 bg-transparent p-0"
                            placeholder="Where are you going?"
                            style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                        />
                        {city && city !== 'Near Me' ? (
                            <button
                                onClick={() => setCity('')}
                                className="btn btn-sm text-gray-400 p-0 border-0 ms-2"
                                style={{ background: 'none' }}
                            >
                                <i className="bi bi-x-circle-fill"></i>
                            </button>
                        ) : (
                            <button
                                onClick={handleNearbyClick}
                                disabled={isLoading}
                                className="text-blue-600 hover:text-blue-800 p-0 border-0 ms-2 transition-colors"
                                style={{ background: 'none' }}
                            >
                                {isLoading ? (
                                    <div className="spinner-border spinner-border-sm text-blue-600" role="status"></div>
                                ) : (
                                    <i className="fas fa-crosshairs"></i>
                                )}
                            </button>
                        )}
                    </div>

                    {showSuggestions && (
                        <div className="position-absolute bg-white border border-gray-100 rounded-3 shadow-lg w-100 z-3 mt-1 dropdown-animation p-2" style={{ top: '100%' }}>
                            {isSearchingPlaces ? (
                                <div className="px-3 py-3 text-center text-muted small">
                                    <div className="spinner-border spinner-border-sm text-blue-600 me-2" role="status"></div>
                                    Finding matching places...
                                </div>
                            ) : city && city !== 'Near Me' && suggestions.length > 0 ? (
                                suggestions.map((c, i) => (
                                    <div
                                        key={i}
                                        className="px-3 py-2.5 rounded-2 border-bottom border-gray-50 d-flex align-items-center hover-bg-light"
                                        style={{ cursor: 'pointer' }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setCity(c);
                                            setShowSuggestions(false);
                                        }}
                                    >
                                        <i className="bi bi-geo-alt-fill text-blue-500 me-2.5"></i>
                                        <span className="fw-semibold text-sm text-gray-800">{c}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="py-1">
                                    <div className="text-[10px] uppercase font-bold text-gray-400 px-3 pb-1 tracking-wider">Popular Destinations</div>
                                    <div className="row g-1 p-1">
                                        {popularDestinations.slice(0, 4).map((dest, i) => (
                                            <div key={i} className="col-6">
                                                <div
                                                    className="p-2 border border-gray-100 rounded-xl d-flex align-items-center hover-bg-light cursor-pointer"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setCity(dest.city);
                                                        setShowSuggestions(false);
                                                    }}
                                                >
                                                    <span className="fs-5 me-2">{dest.icon}</span>
                                                    <div className="text-start">
                                                        <div className="fw-bold text-gray-800 text-xs">{dest.city}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Date and Guest Columns - Mobile */}
                <div className="row g-2 mb-3">
                    {/* Date Picker Trigger - Mobile */}
                    <div className="col-6">
                        <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 tracking-wider block">
                            <i className="bi bi-calendar3-fill text-blue-600 me-1"></i>Dates
                        </label>
                        <div
                            className="border border-gray-200 rounded-xl px-3 py-2 shadow-sm bg-gray-50/50 cursor-pointer text-start"
                            onClick={() => {
                                setShowMobileDateSheet(true);
                                setOpenGuest(false);
                            }}
                        >
                            <div className="fw-bold text-gray-800 text-xs">
                                {format(dateRange[0].startDate, 'dd MMM')} - {format(dateRange[0].endDate, 'dd MMM')}
                            </div>
                            <span className="text-[10px] text-gray-400 block font-semibold">
                                {getDaysDifference()} {getDaysDifference() === 1 ? 'night' : 'nights'}
                            </span>
                        </div>
                    </div>

                    {/* Guest Selector Trigger - Mobile */}
                    <div className="col-6 position-relative" ref={mobileGuestRef}>
                        <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 tracking-wider block">
                            <i className="bi bi-people-fill text-blue-600 me-1"></i>Who
                        </label>
                        <div
                            className="border border-gray-200 rounded-xl px-3 py-2 shadow-sm bg-gray-50/50 cursor-pointer text-start"
                            onClick={() => setOpenGuest(!openGuest)}
                        >
                            <div className="fw-bold text-gray-800 text-xs">
                                {rooms} Room{rooms > 1 ? 's' : ''}
                            </div>
                            <span className="text-[10px] text-gray-400 block font-semibold">
                                {guests} Guest{guests > 1 ? 's' : ''}
                            </span>
                        </div>

                        {openGuest && (
                            <div className="position-absolute bg-white border border-gray-200 rounded-3 p-3 z-3 mt-1 dropdown-animation text-black" style={{ top: '100%', right: 0, minWidth: '260px' }}>
                                <div className="mb-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="text-start">
                                            <span className="fw-bold text-gray-800 text-xs">Rooms</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <button
                                                className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                                onClick={() => setRooms(Math.max(1, rooms - 1))}
                                                disabled={rooms <= 1}
                                                style={{ width: '28px', height: '28px' }}
                                            >
                                                <i className="bi bi-dash"></i>
                                            </button>
                                            <span className="fw-bold text-gray-800 mx-2 text-xs">{rooms}</span>
                                            <button
                                                className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                                onClick={() => setRooms(Math.min(5, rooms + 1))}
                                                disabled={rooms >= 5}
                                                style={{ width: '28px', height: '28px' }}
                                            >
                                                <i className="bi bi-plus"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="text-start">
                                            <span className="fw-bold text-gray-800 text-xs">Guests</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <button
                                                className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                                onClick={() => setGuests(Math.max(1, guests - 1))}
                                                disabled={guests <= 1}
                                                style={{ width: '28px', height: '28px' }}
                                            >
                                                <i className="bi bi-dash"></i>
                                            </button>
                                            <span className="fw-bold text-gray-800 mx-2 text-xs">{guests}</span>
                                            <button
                                                className="btn btn-primary text-white btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                                onClick={() => setGuests(Math.min(10, guests + 1))}
                                                disabled={guests >= 10}
                                                style={{ width: '28px', height: '28px' }}
                                            >
                                                <i className="bi bi-plus"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Search Button - Mobile */}
                <button
                    className="btn btn-primary fw-bold w-100 py-2.5 rounded-xl transition-all d-flex align-items-center justify-content-center border-0"
                    onClick={handleSearch}
                    style={{ fontSize: '15px' }}
                >
                    <i className="bi bi-search me-2"></i>Search Hotels
                </button>
            </div>

            {/* Mobile Bottom Sheet for Date Picker */}
            {showMobileDateSheet && (
                <>
                    {/* Overlay */}
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-block d-md-none"
                        style={{ zIndex: 9998 }}
                        onClick={() => setShowMobileDateSheet(false)}
                    ></div>

                    {/* Bottom Sheet */}
                    <div
                        className="position-fixed bottom-0 start-0 w-100 bg-white rounded-top-4 d-block d-md-none"
                        style={{
                            zIndex: 9999,
                            animation: 'slideUp 0.3s ease-out',
                            maxHeight: '80vh',
                            overflowY: 'auto'
                        }}
                    >
                        <div className="p-3">
                            {/* Header */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0 fw-bold">Select Dates</h5>
                                <button
                                    className="btn btn-sm text-muted p-0 border-0"
                                    onClick={() => setShowMobileDateSheet(false)}
                                >
                                    <i className="bi bi-x-lg" style={{ fontSize: '18px' }}></i>
                                </button>
                            </div>

                            {/* Date Range Picker */}
                            <DateRange
                                locale={enUS}
                                editableDateInputs={true}
                                onChange={handleDateChange}
                                moveRangeOnFirstSelection={false}
                                ranges={dateRange}
                                months={1}
                                direction="vertical"
                                minDate={new Date()}
                                className="w-100"
                                rangeColors={['#2563eb']}
                            />

                            {/* Bottom Actions */}
                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                                <div>
                                    <div className="fw-semibold">
                                        {format(dateRange[0].startDate, 'dd MMM')} - {format(dateRange[0].endDate, 'dd MMM')}
                                    </div>
                                    <small className="text-muted">
                                        {getDaysDifference()} {getDaysDifference() === 1 ? 'night' : 'nights'}
                                    </small>
                                </div>
                                <button
                                    className="btn btn-primary px-4 py-2 fw-semibold border-0"
                                    onClick={() => setShowMobileDateSheet(false)}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Custom high-performance CSS injection */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes slideUp {
                    from {
                        transform: translateY(100%);
                    }
                    to {
                        transform: translateY(0);
                    }
                }
                
                @keyframes fadeInDropdown {
                    from {
                        opacity: 0;
                        transform: translate3d(0, 10px, 0);
                    }
                    to {
                        opacity: 1;
                        transform: translate3d(0, 0, 0);
                    }
                }

                .dropdown-animation {
                    animation: fadeInDropdown 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                .rounded-top-4 {
                    border-radius: 1.5rem 1.5rem 0 0 !important;
                }

                .hover-bg-light:hover {
                    background-color: #f8f9fa !important;
                }
                
                .search-bar-desktop-pill {
                    height: 64px;
                    border: 1px solid #f1f5f9 !important;
                    transition: box-shadow 0.2s ease, border-color 0.2s ease;
                }
                
                .search-bar-compact {
                    height: 48px !important;
                }
                
                .search-bar-compact .search-bar-section {
                    padding-top: 0.25rem !important;
                    padding-bottom: 0.25rem !important;
                }
                
                .search-bar-compact .search-action-round-btn {
                    width: 36px !important;
                    height: 36px !important;
                }
                
                .search-bar-compact .search-bar-divider {
                    height: 24px !important;
                }
                
                .search-bar-desktop-pill:hover {
                    border-color: #cbd5e1 !important;
                }
                
                .search-bar-section {
                    transition: background-color 0.15s ease;
                }
                
                .search-bar-section:hover {
                    background-color: #f8fafc;
                }
                
                .search-bar-section-active {
                    background-color: #f1f5f9 !important;
                }
                
                .search-bar-divider {
                    width: 1px;
                    height: 32px;
                    background-color: #f1f5f9;
                    align-self: center;
                }
                
                .search-action-round-btn {
                    width: 48px;
                    height: 48px;
                    background: #2563eb !important;
                    border: none !important;
                    transition: transform 0.2s ease, background-color 0.2s ease;
                }
                
                .search-action-round-btn:hover {
                    transform: scale(1.05);
                    background: #1d4ed8 !important;
                }
                
                .search-bar-mobile-card {
                    transition: box-shadow 0.2s ease;
                }
                
                .search-bar-mobile-card:focus-within {
                    border-color: #cbd5e1 !important;
                }
            `}} />
        </div>
    );
}

export default HotelSearchBar;